import { Link } from 'react-router-dom'
import { Mic, Calendar, Brain, X, Check } from 'lucide-react'
import Section, { SectionHeader } from '../components/Section'
import Button from '../components/Button'
import { FeatureCard, TestimonialCard } from '../components/Card'

export default function Home() {
    return (
        <>
            {/* Hero Section */}
            <Section className="pt-12 lg:pt-20">
                <div className="grid grid-cols-1 lg:grid-cols-2 items-center gap-16">
                    {/* Left - Content */}
                    <div className="flex flex-col gap-8">
                        <div className="flex flex-col gap-6">
                            <h1 className="text-4xl lg:text-5xl font-bold text-primary-color leading-tight">
                                Your AI Chief of Staff. Finally.
                            </h1>
                            <p className="text-lg lg:text-xl font-normal text-secondary-color leading-relaxed">
                                Capture thoughts at the speed of speech. Let AI organize your life while you focus on what matters.
                            </p>
                        </div>

                        <div className="flex flex-wrap items-center gap-6">
                            <Button to="/signup" size="lg">
                                Start Your 14-Day Free Trial
                            </Button>
                            <Link
                                to="/how-it-works"
                                className="text-lg font-semibold text-primary-base hover:text-primary-darker transition-colors"
                            >
                                See How It Works
                            </Link>
                        </div>

                        <p className="text-sm font-normal text-tertiary-color">
                            No credit card required • Full access during trial
                        </p>
                    </div>

                    {/* Right - Hero Visual */}
                    <div className="flex justify-center items-center">
                        <div className="glass-prominent flex justify-center items-center w-full h-96 p-8 rounded-2xl">
                            <div className="w-full h-full bg-gradient-to-br from-primary-base/20 to-accent-cyan/20 rounded-lg flex items-center justify-center">
                                <div className="text-center">
                                    <div className="w-20 h-20 mx-auto mb-4 rounded-full gradient-primary flex items-center justify-center">
                                        <Mic className="w-10 h-10 text-white/85" />
                                    </div>
                                    <p className="text-secondary-color">Voice to Action</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Section>

            {/* Value Proposition Section */}
            <Section glass>
                <SectionHeader
                    title="Stop Managing Your Tools. Start Living Your Life."
                />
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    <FeatureCard
                        icon={<Mic className="w-full h-full" />}
                        title="Talk, Don't Type"
                        description="Capture ideas while walking, driving, or thinking. Your AI remembers everything and organizes it instantly."
                    />
                    <FeatureCard
                        icon={<Calendar className="w-full h-full" />}
                        title="It Plans Your Day"
                        description="No more manual time-blocking. The AI schedules your tasks, adapts to reality, and asks before moving things around."
                    />
                    <FeatureCard
                        icon={<Brain className="w-full h-full" />}
                        title="It Remembers Context"
                        description="Unlike Siri or Alexa, Way2Will knows your history, projects, and people. No more repeating yourself."
                    />
                </div>
            </Section>

            {/* Problem/Solution Section */}
            <Section>
                <SectionHeader
                    title="The Tools You Use Are Weighing You Down"
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                    {/* Traditional Tools */}
                    <div className="glass shadow-glass p-8 rounded-2xl">
                        <h3 className="text-2xl font-semibold text-primary-color text-center mb-8">
                            Traditional Tools
                        </h3>
                        <div className="flex flex-col gap-6">
                            {[
                                "Type everything manually into to-do lists",
                                "Spend hours organizing tasks across multiple apps",
                                "Rigid algorithms that create more anxiety",
                                "No memory of past conversations or context"
                            ].map((item, index) => (
                                <div key={index} className="flex items-start gap-4">
                                    <div className="flex justify-center items-center w-6 h-6 mt-1">
                                        <X className="w-5 h-5 text-error" />
                                    </div>
                                    <p className="text-base font-normal text-secondary-color">{item}</p>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Way2Will */}
                    <div className="glass-elevated p-8 rounded-2xl">
                        <h3 className="text-2xl font-semibold text-primary-color text-center mb-8">
                            Way2Will
                        </h3>
                        <div className="flex flex-col gap-6">
                            {[
                                "Speak naturally - AI extracts tasks, events, and notes",
                                "Automatic organization across your entire workflow",
                                "Flexible scheduling that negotiates with you",
                                "Long-term memory that builds on every interaction"
                            ].map((item, index) => (
                                <div key={index} className="flex items-start gap-4">
                                    <div className="flex justify-center items-center w-6 h-6 mt-1">
                                        <Check className="w-5 h-5 text-success" />
                                    </div>
                                    <p className="text-base font-normal text-secondary-color">{item}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </Section>

            {/* Social Proof Section */}
            <Section glass>
                <SectionHeader
                    title="Trusted by Founders, Creators, and High-Performers"
                />
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    <TestimonialCard
                        quote="Way2Will feels like having an executive assistant in my pocket. I can finally capture ideas without breaking flow."
                        name="Sarah Chen"
                        role="Founder @ TechStart"
                    />
                    <TestimonialCard
                        quote="As someone with ADHD, this is the first productivity tool that doesn't make me feel overwhelmed. It's like a supportive body double."
                        name="Marcus Johnson"
                        role="Content Creator"
                    />
                    <TestimonialCard
                        quote="I was paying Motion $34/month and still felt like I was fighting the system. Way2Will actually listens."
                        name="Emily Rodriguez"
                        role="Product Manager"
                    />
                </div>

                {/* Featured In */}
                <div className="flex justify-center items-center gap-12 pt-8 mt-8 opacity-60">
                    <span className="text-sm font-normal text-tertiary-color">As featured in</span>
                    <div className="flex items-center gap-8">
                        <span className="text-tertiary-color font-semibold">TechCrunch</span>
                        <span className="text-tertiary-color font-semibold">Product Hunt</span>
                        <span className="text-tertiary-color font-semibold">Wired</span>
                    </div>
                </div>
            </Section>

            {/* CTA Section */}
            <Section>
                <div className="max-w-4xl mx-auto">
                    <div className="glass-prominent text-center flex flex-col items-center gap-8 p-12 lg:p-16 rounded-2xl">
                        <div className="flex flex-col gap-6">
                            <h2 className="text-3xl lg:text-4xl font-semibold text-primary-color">
                                Experience Antigravity Productivity
                            </h2>
                            <p className="text-lg lg:text-xl font-normal text-secondary-color">
                                Join thousands of professionals who've replaced their to-do lists with an AI Chief of Staff.
                            </p>
                        </div>
                        <div className="flex flex-col items-center gap-6">
                            <Button to="/signup" size="lg">
                                Start Your Free 14-Day Trial
                            </Button>
                            <p className="text-base font-normal text-tertiary-color">
                                Full access to Voice Capture, Auto-Scheduler, and Chief of Staff Memory. No credit card required.
                            </p>
                        </div>
                    </div>
                </div>
            </Section>
        </>
    )
}
